import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import * as moment from 'moment';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { EMAIL, MOBILE } from 'src/app/utils/items/contactType';
import { sex } from 'src/app/utils/items/sex';
import { OUTPATIENT, OUTPATIENT_CLASS_UID } from 'src/app/utils/items/visit';
import { AppService } from 'src/app/utils/services/app.service';
import { DoctorService } from 'src/app/utils/services/doctor/doctor.service';
import { PatientService } from 'src/app/utils/services/mpi/patient.service';
import { VisitService } from 'src/app/utils/services/mpi/visit.service';
import { ScheduleService } from 'src/app/utils/services/schedule/schedule.service';

const QUICK_CONSULTATION_CLINIC_CODE = 'quick-consultation';
const QUICK_CONSULTATION_CLINIC_NAME = 'Quick Consultation';
const QUICK_CONSULTATION_DOCTOR_INCLUDES = 'hmo,hospital_affiliations.hospital_master,clinics.resource.services.service_item';
type QuickPatientFlow = 'consultation' | 'service';

export interface QuickPatientTemplateSelection {
  id?: number | string;
  code: string;
  name: string;
}

export interface QuickPatientModalConfig {
  flow?: QuickPatientFlow;
  selectedTemplate?: QuickPatientTemplateSelection | null;
}

function dateOfBirthValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  return moment(control.value).isAfter(moment(), 'day') ? { invalidDate: true } : null;
}

function mobileNumberValidator(control: AbstractControl): ValidationErrors | null {
  if (!control.value) {
    return null;
  }

  const digits = String(control.value).replace(/\D/g, '');
  let normalized = digits;

  if (normalized.startsWith('63') && normalized.length === 12) {
    normalized = normalized.slice(2);
  }

  if (normalized.startsWith('0') && normalized.length === 11) {
    normalized = normalized.slice(1);
  }

  return /^\d{10}$/.test(normalized) ? null : { invalidMobile: true };
}

@Component({
  selector: 'app-quick-patient-modal',
  templateUrl: './quick-patient-modal.component.html',
  styleUrls: ['./quick-patient-modal.component.scss']
})
export class QuickPatientModalComponent implements OnInit {

  modal = false;
  confirmationVisible = false;
  searching = false;
  processing = false;
  searchSubmitted = false;
  showCreateForm = false;
  searchResults: any = null;
  page = 1;
  readonly perPage = 5;
  readonly sexOptions = sex;
  flow: QuickPatientFlow = 'consultation';
  selectedTemplate: QuickPatientTemplateSelection | null = null;

  currentUser: any;
  pendingSelectedPatient: any = null;
  searchForm: FormGroup;
  patientForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private toastr: ToastrService,
    private appService: AppService,
    private doctorService: DoctorService,
    private patientService: PatientService,
    private visitService: VisitService,
    private scheduleService: ScheduleService
  ) { }

  ngOnInit(): void {
    this.currentUser = this.appService.getCurrentUser();
    this.createForms();
  }

  createForms(): void {
    this.searchForm = this.fb.group({
      firstName: [null],
      lastName: [null],
    });

    this.patientForm = this.fb.group({
      prefix: [null],
      firstName: [null, [Validators.required]],
      middleName: [null],
      lastName: [null, [Validators.required]],
      suffix: [null],
      nickName: [null],
      sex: [null, [Validators.required]],
      dateOfBirth: [null, [Validators.required, dateOfBirthValidator]],
      age: [{ value: null, disabled: true }],
      emailAddress: [null, [Validators.email]],
      mobileNumber: [null, [mobileNumberValidator]],
    });
  }

  get isServiceFlow(): boolean {
    return this.flow === 'service';
  }

  get modalTitle(): string {
    return this.isServiceFlow ? 'Quick Services' : 'Quick Consultation';
  }

  get createFormDescription(): string {
    if (this.isServiceFlow) {
      return `Enter minimal patient information, then continue to ${this.selectedTemplate?.name || 'the selected document'}.`;
    }

    return 'Enter minimal patient information, then continue to clinical documentation.';
  }

  get calloutDescription(): string {
    if (this.isServiceFlow) {
      return 'Create a new patient record with only the minimum information needed for this document.';
    }

    return 'Create a new patient record with only the minimum information needed for consultation.';
  }

  onOpen(config?: QuickPatientModalConfig): void {
    this.currentUser = this.appService.getCurrentUser();
    this.flow = config?.flow || 'consultation';
    this.selectedTemplate = config?.selectedTemplate || null;

    if (this.getSelectedCompanyId() !== 1) {
      this.toastr.warning(`${this.modalTitle} is only available when the selected company is mWell.`);
      return;
    }

    if (!this.needsQuickConsultationContextRefresh()) {
      this.modal = true;
      this.resetState();
      return;
    }

    this.processing = true;

    this.doctorService.getDoctorByUser(this.currentUser?.id, {
      includes: QUICK_CONSULTATION_DOCTOR_INCLUDES,
    }).subscribe((doctor: any) => {
      this.currentUser = {
        ...this.currentUser,
        doctor,
      };

      this.appService.storeUser(this.currentUser);
      this.appService.setUser(this.currentUser);

      if (!this.getQuickConsultationClinic()) {
        this.processing = false;
        this.toastr.error(`${this.modalTitle} clinic could not be prepared automatically.`);
        return;
      }

      this.modal = true;
      this.resetState();
    }, () => {
      this.processing = false;
      this.toastr.error(`Unable to load the ${this.modalTitle.toLowerCase()} clinic right now.`);
    });
  }

  onClose(): void {
    if (this.processing) {
      return;
    }

    this.modal = false;
  }

  resetState(): void {
    this.confirmationVisible = false;
    this.searching = false;
    this.processing = false;
    this.pendingSelectedPatient = null;
    this.searchSubmitted = false;
    this.showCreateForm = false;
    this.searchResults = null;
    this.page = 1;
    this.searchForm.reset();
    this.patientForm.reset();
    this.patientForm.get('age')?.setValue(null);
  }

  get canSearch(): boolean {
    return !!this.searchForm.value.firstName?.trim() || !!this.searchForm.value.lastName?.trim();
  }

  get patientControls(): any {
    return this.patientForm.controls;
  }

  search(page = 1): void {
    this.searchSubmitted = true;

    if (!this.canSearch || this.searching) {
      return;
    }

    const queryParams: any = {
      page: this.page = page,
      perPage: this.perPage,
      order: 'last',
      sort: 'asc',
      md_id: this.currentUser?.doctor?.md_id,
      fn: this.searchForm.value.firstName?.trim(),
      ln: this.searchForm.value.lastName?.trim(),
    };

    this.searching = true;

    this.patientService.searchPatients(queryParams).subscribe((response: any) => {
      this.searchResults = response;
      this.searching = false;
    }, () => {
      this.searching = false;
      this.toastr.error('Unable to search patients right now.');
    });
  }

  clearSearch(): void {
    this.searchSubmitted = false;
    this.searchResults = null;
    this.page = 1;
    this.searchForm.reset();
  }

  openCreateForm(): void {
    this.showCreateForm = true;
    this.patientForm.patchValue({
      firstName: this.searchForm.value.firstName?.trim() || null,
      lastName: this.searchForm.value.lastName?.trim() || null,
    });
  }

  backToSearch(): void {
    this.showCreateForm = false;
  }

  updateAge(): void {
    const dateOfBirth = this.patientForm.get('dateOfBirth')?.value;
    const age = dateOfBirth ? moment().diff(moment(dateOfBirth), 'years') : null;
    this.patientForm.get('age')?.setValue(age);
  }

  selectPatient(patient: any): void {
    if (this.processing) {
      return;
    }

    this.pendingSelectedPatient = patient;
    this.confirmationVisible = true;
  }

  confirmSelectedPatient(): void {
    if (this.processing || !this.pendingSelectedPatient?.patient_id) {
      return;
    }

    this.confirmationVisible = false;
    this.processing = true;
    this.ensureOutpatientVisit(this.pendingSelectedPatient?.patient_id);
  }

  cancelSelectedPatient(): void {
    if (this.processing) {
      return;
    }

    this.confirmationVisible = false;
    this.pendingSelectedPatient = null;
  }

  savePatient(): void {
    if (this.processing) {
      return;
    }

    if (this.patientForm.invalid) {
      this.patientForm.markAllAsTouched();
      return;
    }

    const payload = this.buildPatientPayload();
    this.processing = true;

    this.patientService.registerPatient_confirm(payload).subscribe((response: any) => {
      if (response?.message === 'Record already exists' && confirm('A similar patient already exists. Do you want to continue with this patient registration?') !== true) {
        this.processing = false;
        return;
      }

      this.registerPatient(payload);
    }, () => {
      this.processing = false;
      this.toastr.error('Unable to validate the patient details right now.');
    });
  }

  private registerPatient(payload: any): void {
    this.patientService.registerPatient(payload).subscribe((response: any) => {
      this.toastr.success('Successfully created the patient record.');
      this.ensureOutpatientVisit(response?.patient_id);
    }, () => {
      this.processing = false;
      this.toastr.error('Unable to save the patient record right now.');
    });
  }

  private ensureOutpatientVisit(patientId: string): void {
    const selectedCompanyId = this.getSelectedCompanyId();

    if (selectedCompanyId !== 1) {
      this.processing = false;
      this.toastr.warning(`${this.modalTitle} is only available when the selected company is mWell.`);
      return;
    }

    this.visitService.currentVisit(patientId, { includes: 'appointment' }).subscribe((visit: any) => {
      const visitCompanyId = visit?.company_id ?? visit?.appointment?.company_id;
      const sameCompanyVisit = !selectedCompanyId || +visitCompanyId === +selectedCompanyId;
      const sameDayVisit = this.isSameDayVisit(visit);

      if (visit?.visit_id && visit?.patient_class_code === OUTPATIENT && sameCompanyVisit && sameDayVisit) {
        this.ensureQuickAppointment(patientId, visit);
        return;
      }

      if (visit?.visit_id && visit?.patient_class_code === OUTPATIENT && sameCompanyVisit && !sameDayVisit) {
        this.closePreviousVisitAndCreateNew(patientId, visit);
        return;
      }

      this.createOutpatientVisit(patientId);
    }, () => {
      this.createOutpatientVisit(patientId);
    });
  }

  private createOutpatientVisit(patientId: string): void {
    const clinic = this.getQuickConsultationClinic();
    const companyId = this.getSelectedCompanyId();
    const payload: any = {
      visit: {
        patient_id: patientId,
        md_id: this.currentUser?.doctor?.md_id,
        clinic_id: clinic?.id || null,
        type_uid: 125100,
        priority_uid: 'S',
        priority_code: 126100,
        patient_class_uid: OUTPATIENT_CLASS_UID,
        patient_class_code: OUTPATIENT,
        admission_datetime: moment().format('YYYY-MM-DD HH:mm:ss'),
        company_id: companyId,
      },
      transaction: {
        patient_class_uid: OUTPATIENT_CLASS_UID,
        patient_class_code: OUTPATIENT,
        patient_class_text: 'Out-Patient',
        patient_class_display: 'Out-Patient',
      },
    };

    this.patientService.registerPatientVisit(patientId, payload).subscribe((visit: any) => {
      this.ensureQuickAppointment(patientId, visit);
    }, () => {
      this.processing = false;
      this.modal = false;
      this.toastr.warning(`Patient is ready, but the ${this.modalTitle.toLowerCase()} visit could not be created automatically.`);
      this.navigateToClinicalDocumentation(patientId);
    });
  }

  private completeQuickFlow(patientId: string, visitId?: string): void {
    this.processing = false;
    this.modal = false;

    if (this.isServiceFlow && visitId && this.selectedTemplate?.code) {
      this.router.navigate([
        '/patient',
        patientId,
        'visit',
        visitId,
        'document',
        this.selectedTemplate.code,
      ], {
        queryParams: { source: 'quick-services' }
      });
      return;
    }

    this.navigateToClinicalDocumentation(patientId, visitId);
  }

  private navigateToClinicalDocumentation(patientId: string, visitId?: string): void {
    this.router.navigate(['/patient', patientId, 'clinical-documentation'], {
      queryParams: visitId ? { visit_id: visitId } : {}
    });
  }

  private ensureQuickAppointment(patientId: string, visit: any): void {
    const selectedCompanyId = this.getSelectedCompanyId();

    if (selectedCompanyId !== 1) {
      this.completeQuickFlow(patientId, visit?.visit_id);
      return;
    }

    const appointmentMatchesCompany = visit?.appointment?.id
      && (+visit?.appointment?.company_id === +selectedCompanyId);

    if (appointmentMatchesCompany) {
      this.completeQuickFlow(patientId, visit?.visit_id);
      return;
    }

    const clinic = this.getQuickConsultationClinic();
    const service = this.getDefaultService(clinic);

    if (!clinic || !service) {
      this.handleQuickAppointmentFallback(patientId, visit?.visit_id);
      return;
    }

    const date = moment().format('YYYY-MM-DD');

    if (+service?.is_auto_booking === 1 || service?.is_auto_booking === true) {
      this.bookQuickAppointment(patientId, visit, clinic, service, null, date);
      return;
    }

    this.scheduleService.getServiceSchedules(service?.service_item_code, {
      date,
      resource_id: clinic?.resource?.id,
      clinic_id: clinic?.id,
    }).subscribe((response: any) => {
      const slot = response?.slots?.[0];

      if (!slot?.id) {
        this.handleQuickAppointmentFallback(patientId, visit?.visit_id);
        return;
      }

      this.bookQuickAppointment(patientId, visit, clinic, service, slot.id, date);
    }, () => {
      this.handleQuickAppointmentFallback(patientId, visit?.visit_id);
    });
  }

  private bookQuickAppointment(
    patientId: string,
    visit: any,
    clinic: any,
    service: any,
    slotId: number | null,
    date: string
  ): void {
    const params: any = {
      patient_id: patientId,
      service_id: service?.id,
      resource_id: service?.resource_id || clinic?.resource?.id,
      slot_id: slotId,
      complaint: visit?.chief_complaint || this.getQuickAppointmentComplaint(),
      date,
      host_name: this.currentUser?.doctor?.primary_name?.full,
      host_email: this.currentUser?.email,
      add_zoom_link: false,
      company_id: this.getSelectedCompanyId(),
      visit_id: visit?.visit_id,
    };

    this.scheduleService.book(params).subscribe(() => {
      this.completeQuickFlow(patientId, visit?.visit_id);
    }, () => {
      this.handleQuickAppointmentFallback(patientId, visit?.visit_id);
    });
  }

  private handleQuickAppointmentFallback(patientId: string, visitId?: string): void {
    this.toastr.warning(`Patient and visit are ready, but the ${this.modalTitle.toLowerCase()} appointment could not be created automatically.`);
    this.completeQuickFlow(patientId, visitId);
  }

  private closePreviousVisitAndCreateNew(patientId: string, visit: any): void {
    this.visitService.updateDischargeDateTime(visit?.visit_id).subscribe(() => {
      this.createOutpatientVisit(patientId);
    }, () => {
      this.processing = false;
      this.toastr.error('Unable to close the previous open visit automatically. Please end the previous visit first.');
    });
  }

  private buildPatientPayload(): any {
    const values = this.patientForm.getRawValue();
    const mobileNumber = this.normalizeMobile(values.mobileNumber);

    return {
      md_id: this.currentUser?.doctor?.md_id,
      patient: {
        md_id: this.currentUser?.doctor?.md_id,
        clinic_id: this.getQuickConsultationClinic()?.id || null,
        nickname: this.nullIfEmpty(values.nickName),
        sex_code: values.sex,
        date_of_birth: moment(values.dateOfBirth).utc(true).format('YYYY-MM-DD'),
        is_newborn: false,
        is_fictitious_date_of_birth: false,
      },
      name: {
        prefix: this.nullIfEmpty(values.prefix),
        first: values.firstName?.trim(),
        middle: this.nullIfEmpty(values.middleName),
        last: values.lastName?.trim(),
        suffix: this.nullIfEmpty(values.suffix),
        is_primary: true,
        type_code: 'P',
        type_text: 'Primary',
        type_display: 'Primary',
      },
      contacts: [
        ...(values.emailAddress ? [{
          type_code: EMAIL,
          contact_details: values.emailAddress?.trim(),
          description: null,
          is_primary: 1,
        }] : []),
        ...(mobileNumber ? [{
          type_code: MOBILE,
          contact_details: mobileNumber,
          description: null,
          is_primary: values.emailAddress ? 0 : 1,
        }] : []),
      ],
      attributes: [],
      addresses: [],
      identifiers: [],
      families: [],
    };
  }

  private needsQuickConsultationContextRefresh(): boolean {
    const clinic = this.getQuickConsultationClinic();

    return !clinic || !clinic?.resource || !clinic?.resource?.services?.length;
  }

  private getQuickConsultationClinic(): any {
    return this.currentUser?.doctor?.clinics?.find((clinic) =>
      clinic?.name_code === QUICK_CONSULTATION_CLINIC_CODE
      || clinic?.name_display === QUICK_CONSULTATION_CLINIC_NAME
      || clinic?.name_text === QUICK_CONSULTATION_CLINIC_NAME
    ) || null;
  }

  private getDefaultService(clinic: any): any {
    const services = clinic?.resource?.services || [];

    return services.find((service) => +service?.is_auto_booking === 1 || service?.is_auto_booking === true)
      || services[0]
      || null;
  }

  private getQuickAppointmentComplaint(): string {
    return this.isServiceFlow
      ? this.selectedTemplate?.name || 'Quick service'
      : 'Quick consultation';
  }

  private isSameDayVisit(visit: any): boolean {
    if (!visit?.admission_datetime) {
      return false;
    }

    return moment(visit.admission_datetime).isSame(moment(), 'day');
  }

  private getSelectedCompanyId(): number | null {
    try {
      const selectedCompany = localStorage.getItem('selectedCompany');

      if (selectedCompany) {
        return JSON.parse(selectedCompany)?.id ?? null;
      }
    } catch (e) {
      return this.currentUser?.doctor?.is_onelife ? 2 : 1;
    }

    return this.currentUser?.doctor?.is_onelife ? 2 : 1;
  }

  private normalizeMobile(value: string): string | null {
    if (!value) {
      return null;
    }

    let digits = String(value).replace(/\D/g, '');

    if (digits.startsWith('63') && digits.length === 12) {
      digits = digits.slice(2);
    }

    if (digits.startsWith('0') && digits.length === 11) {
      digits = digits.slice(1);
    }

    return digits || null;
  }

  private nullIfEmpty(value: string): string | null {
    return value?.trim?.() ? value.trim() : null;
  }
}
